import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAdminAuth } from './auth/AdminAuthProvider'
import AdminLayout from './components/AdminLayout'
import AccessState from './components/AccessState'
import LoginPage from './pages/LoginPage'
import KakaoCallbackPage from './pages/KakaoCallbackPage'
import DashboardPage from './pages/DashboardPage'
import AnalyticsPage from './pages/AnalyticsPage'
import GrowthPage from './pages/GrowthPage'
import AnnouncementsPage from './pages/AnnouncementsPage'
import AnnouncementEditorPage from './pages/AnnouncementEditorPage'
import FaqPage from './pages/FaqPage'
import HomeConfigPage from './pages/HomeConfigPage'
import ShuttlePage from './pages/ShuttlePage'
import MapPage from './pages/MapPage'
import CafeteriaNoticePage from './pages/CafeteriaNoticePage'
import FeatureSettingsPage from './pages/FeatureSettingsPage'
import AdminRolesPage from './pages/AdminRolesPage'
import DataVersionsPage from './pages/DataVersionsPage'
import ReportsPage from './pages/ReportsPage'
import NotificationsPage from './pages/NotificationsPage'
import AuditPage from './pages/AuditPage'
import { MobileMonitorFeaturesPage, MobileMonitorTodayPage, MobileMonitorUsersPage } from './pages/MobileMonitorPages'
import MobileReadOnlyState from './components/MobileReadOnlyState'
import { safeAdminReturnPath } from './lib/routing'
import { MOBILE_MONITOR_QUERY, useMediaQuery } from './lib/responsive'
import { adminCapabilities } from './auth/permissions'

function LoginRoute() {
  const auth = useAdminAuth()
  const location = useLocation()
  if (['loading', 'checking'].includes(auth.status)) return <AccessState kind="loading" />
  if (auth.status === 'forbidden') return <AccessState kind="forbidden" detail={auth.user?.email} onAction={auth.logout} />
  if (auth.status === 'error') return <AccessState kind="error" detail={auth.error?.message} onAction={auth.logout} />
  if (auth.status === 'authorized') return <Navigate to={safeAdminReturnPath(location.state?.from)} replace />
  return <LoginPage />
}

function ProtectedRoutes() {
  const auth = useAdminAuth()
  const location = useLocation()
  const isMobileMonitor = useMediaQuery(MOBILE_MONITOR_QUERY)
  const { canViewAdminRoles, canWrite } = adminCapabilities(auth.role)

  if (['loading', 'checking'].includes(auth.status)) return <AccessState kind="loading" />
  if (auth.status === 'configuration-error') return <AccessState kind="configuration" />
  if (auth.status === 'error') return <AccessState kind="error" detail={auth.error?.message} onAction={auth.logout} />
  if (auth.status === 'signed-out') return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />
  if (auth.status === 'forbidden') return <AccessState kind="forbidden" detail={auth.user?.email} onAction={auth.logout} />
  const monitorPath = location.pathname === '/' || location.pathname === '/dashboard' || location.pathname === '/analytics' || location.pathname === '/monitor/features'
  if (isMobileMonitor && !monitorPath) return <MobileReadOnlyState onLogout={auth.logout} />

  return (
    <AdminLayout monitor={isMobileMonitor}>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={isMobileMonitor ? <MobileMonitorTodayPage /> : <DashboardPage />} />
        <Route path="/analytics" element={isMobileMonitor ? <MobileMonitorUsersPage /> : <AnalyticsPage />} />
        <Route path="/growth" element={<GrowthPage />} />
        <Route path="/monitor/features" element={isMobileMonitor ? <MobileMonitorFeaturesPage /> : <Navigate to="/dashboard" replace />} />
        <Route path="/announcements" element={<AnnouncementsPage />} />
        <Route path="/announcements/new" element={canWrite ? <AnnouncementEditorPage /> : <Navigate to="/announcements" replace />} />
        <Route path="/announcements/:announcementId" element={<AnnouncementEditorPage />} />
        <Route path="/faq" element={<FaqPage />} />
        <Route path="/home-config" element={<HomeConfigPage />} />
        <Route path="/shuttle" element={<ShuttlePage />} />
        <Route path="/map" element={<MapPage />} />
        <Route path="/cafeteria-notice" element={<CafeteriaNoticePage />} />
        <Route path="/feature-settings" element={<FeatureSettingsPage />} />
        <Route path="/admin-roles" element={canViewAdminRoles ? <AdminRolesPage /> : <Navigate to="/dashboard" replace />} />
        <Route path="/data-versions" element={<DataVersionsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/:reportId" element={<ReportsPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/audit" element={<AuditPage />} />
        <Route path="*" element={<AccessState kind="not-found" />} />
      </Routes>
    </AdminLayout>
  )
}

export default function App() {
  return (
    <Routes>
      <Route path="/auth/kakao/callback" element={<KakaoCallbackPage />} />
      <Route path="/login" element={<LoginRoute />} />
      <Route path="*" element={<ProtectedRoutes />} />
    </Routes>
  )
}
